import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';

import { AuthAppService } from '../auth-app/auth-app.service';
import { AUTHAPIs } from '../authorized-apis';
import { Constants } from '../constants';

@Injectable()
export class JWTAuthInterceptor implements HttpInterceptor {
  constructor(
    private auth: AuthAppService,
    private router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Bypass interceptor for third-party endpoints that reject custom headers.
    if (req.url.includes('cloudinary.com') || req.url.includes('formspree.io')) {
      return next.handle(req);
    }

    const isFormDataBody = this.isFormDataBody(req.body);
    const browserUrl = this.router.url;
    const role = this.auth.getViewType();
    const businessUuid = this.auth.getBusinessUUID();

    const workforceToken = this.isWorkforceApi(req.url) ? this.auth.getWorkforceToken()?.token : null;

    if (workforceToken) {
      // Workforce platform calls use the workforce-scoped Bearer token (minted by WorkforceTokenService), not the Avaana JWT.
      let headers = req.headers.set('Authorization', `Bearer ${workforceToken}`);
      if (!isFormDataBody) {
        headers = headers.set('Content-Type', 'application/json');
      }
      req = req.clone({ headers });
    } else if (this.auth.getToken()) {
      let headers = req.headers.set('Authorization', `JWT ${this.auth.getToken()}`);
      if (!(req.body instanceof FormData)) {
        headers = headers.set('Content-Type', 'application/json');
      }

      if (
        browserUrl.includes('/business/') ||
        browserUrl.includes('/business-bookings/') ||
        browserUrl.includes('/workcover/bookings/')
      ) {
        if (businessUuid) {
          headers = headers.set('Business-Uuid', businessUuid);
        }
        if (role) {
          headers = headers.set('Role', role);
        }
      }

      req = req.clone({ headers });
    } else if (req.url.includes('/login') && typeof req.body === 'string') {
      const headers = req.headers.set('encrypt', 'true').set('decrypt', 'true').set('Content-Type', 'application/json');
      req = req.clone({ headers, responseType: 'text' as 'json' });
    } else if (
      req.url.includes('api-qa-logichealth') ||
      req.url.includes('api-qa-eml') ||
      req.url.includes('api-qa-trinity') ||
      req.url.includes('api-qa-hii')
    ) {
      let headers = req.headers;
      if (!isFormDataBody) {
        headers = headers.set('Content-Type', 'application/json');
      }
      req = req.clone({ headers });
    } else {
      const headers = req.headers.set('encrypt', 'false').set('decrypt', 'false');
      const requestHeaders = isFormDataBody ? headers : headers.set('Content-Type', 'application/json');
      req = req.clone({ headers: requestHeaders });
    }

    return next.handle(req);
  }

  // Every route on the workforce host takes the workforce Bearer token, not just /api/v1/platform/
  // (e.g. /api/v1/eip/ and /api/v1/preventive-health/ extension requests). The token exchange itself is on BASE_URL.
  private isWorkforceApi(url: string): boolean {
    return !!Constants.WORKFORCE_URL && url.startsWith(Constants.WORKFORCE_URL);
  }

  private isFormDataBody(body: unknown): boolean {
    return typeof FormData !== 'undefined' && body instanceof FormData;
  }

  isJWTNeeded(url: string): boolean {
    const authApiList = AUTHAPIs.AUTHORIZATIONAPILIST;
    const list = authApiList.filter((x: string) => url.includes(x));
    return list.length > 0;
  }
}
